import { useState, useEffect, useCallback } from 'react';
import { useAuth, ROLES } from '@/context/AuthContext';
import { usersApi, schoolsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import {
  Users,
  UserPlus,
  Pencil,
  Trash2,
  Shield,
  Building2,
  Search,
  Loader2,
  Filter,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const ROLE_CONFIG = {
  superadmin: { label: 'Superadmin', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300', icon: Shield },
  school_admin: { label: 'Skoladmin', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300', icon: Building2 },
  staff: { label: 'Personal', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300', icon: Users },
};

const EMPTY_FORM = { name: '', email: '', password: '', role: 'staff', school_id: '', is_active: true };

export default function UsersPage() {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterSchool, setFilterSchool] = useState('all');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create | edit
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, schoolsRes] = await Promise.all([
        usersApi.list(),
        schoolsApi.list(),
      ]);
      setUsers(usersRes.data);
      setSchools(schoolsRes.data);
    } catch (err) {
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const schoolMap = {};
  schools.forEach(s => { schoolMap[s.id] = s.name; });

  const openCreate = () => {
    setDialogMode('create');
    setFormData(EMPTY_FORM);
    setEditingUserId(null);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setDialogMode('edit');
    setFormData({
      name: u.name || '',
      email: u.email,
      password: '',
      role: u.role,
      school_id: u.school_id || '',
      is_active: u.is_active,
    });
    setEditingUserId(u.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Namn och e-post krävs');
      return;
    }
    if (dialogMode === 'create' && !formData.password) {
      toast.error('Lösenord krävs vid skapande');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role,
        school_id: formData.school_id || null,
      };

      if (dialogMode === 'create') {
        payload.password = formData.password;
        await usersApi.create(payload);
        toast.success('Användare skapad');
      } else {
        if (formData.password) payload.password = formData.password;
        payload.is_active = formData.is_active;
        await usersApi.update(editingUserId, payload);
        toast.success('Användare uppdaterad');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (u) => {
    setDeletingUser(u);
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    setDeleting(true);
    try {
      await usersApi.delete(deletingUser.id);
      toast.success('Användare borttagen');
      setDeleteDialog(false);
      setDeletingUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort');
    } finally {
      setDeleting(false);
    }
  };

  // Filtered users
  const filtered = users.filter(u => {
    if (search && !u.name?.toLowerCase().includes(search.toLowerCase()) && !u.email?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterSchool !== 'all') {
      if (filterSchool === 'none' && u.school_id) return false;
      if (filterSchool !== 'none' && u.school_id !== filterSchool) return false;
    }
    return true;
  });

  const stats = {
    total: users.length,
    superadmins: users.filter(u => u.role === 'superadmin').length,
    schoolAdmins: users.filter(u => u.role === 'school_admin').length,
    staff: users.filter(u => u.role === 'staff').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl text-slate-900 dark:text-white">
            Användarhantering
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-ibm mt-1">
            Hantera personal och behörigheter
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-navy hover:bg-navy/90 rounded-sm font-ibm gap-2"
          data-testid="create-user-btn"
        >
          <UserPlus className="h-4 w-4" />
          Lägg till användare
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Totalt', value: stats.total, color: 'text-slate-900 dark:text-white' },
          { label: 'Superadmins', value: stats.superadmins, color: 'text-purple-600 dark:text-purple-400' },
          { label: 'Skoladmins', value: stats.schoolAdmins, color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Personal', value: stats.staff, color: 'text-slate-600 dark:text-slate-300' },
        ].map((s) => (
          <Card key={s.label} className="border-slate-200 dark:border-slate-700">
            <CardContent className="py-4 px-5">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-ibm uppercase tracking-widest">{s.label}</p>
              <p className={cn("text-2xl font-chivo font-black mt-1", s.color)}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="py-4 px-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Sök namn eller e-post..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                data-testid="user-search-input"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="filter-role">
                <Filter className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Roll" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla roller</SelectItem>
                <SelectItem value="superadmin">Superadmin</SelectItem>
                <SelectItem value="school_admin">Skoladmin</SelectItem>
                <SelectItem value="staff">Personal</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterSchool} onValueChange={setFilterSchool}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-school">
                <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Skola" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla skolor</SelectItem>
                <SelectItem value="none">Ej tilldelad</SelectItem>
                {schools.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users table */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 dark:border-slate-700">
                <TableHead className="font-ibm text-xs uppercase tracking-widest">Namn</TableHead>
                <TableHead className="font-ibm text-xs uppercase tracking-widest">E-post</TableHead>
                <TableHead className="font-ibm text-xs uppercase tracking-widest">Roll</TableHead>
                <TableHead className="font-ibm text-xs uppercase tracking-widest hidden md:table-cell">Skola</TableHead>
                <TableHead className="font-ibm text-xs uppercase tracking-widest hidden sm:table-cell">Status</TableHead>
                <TableHead className="font-ibm text-xs uppercase tracking-widest text-right">Åtgärder</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500 dark:text-slate-400 font-ibm">
                    Inga användare hittades
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(u => {
                  const roleConf = ROLE_CONFIG[u.role] || ROLE_CONFIG.staff;
                  const RoleIcon = roleConf.icon;
                  const isCurrentUser = u.id === currentUser?.id;
                  const isSuperadminTarget = u.role === 'superadmin';
                  const canEditThis = isSuperAdmin() || !isSuperadminTarget;
                  return (
                    <TableRow key={u.id} className="border-slate-200 dark:border-slate-700" data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-ibm font-medium text-slate-900 dark:text-white">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-navy dark:bg-slate-600 text-white flex items-center justify-center font-ibm font-semibold text-sm shrink-0">
                            {u.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <span className="truncate">{u.name}</span>
                          {isCurrentUser && (
                            <Badge variant="outline" className="text-[10px] shrink-0">Du</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400 font-ibm text-sm">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={cn("font-ibm text-xs gap-1", roleConf.color)}>
                          <RoleIcon className="h-3 w-3" />
                          {roleConf.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-slate-600 dark:text-slate-400 font-ibm text-sm">
                        {u.school_id ? (schoolMap[u.school_id] || 'Okänd') : (
                          <span className="text-slate-400 dark:text-slate-500">Alla skolor</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant={u.is_active ? 'default' : 'secondary'}
                          className={u.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                          }
                        >
                          {u.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEditThis && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEdit(u)}
                              className="h-8 w-8"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Pencil className="h-4 w-4 text-slate-500" />
                            </Button>
                          )}
                          {!isCurrentUser && canEditThis && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => confirmDelete(u)}
                              className="h-8 w-8 hover:text-red-600"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">
              {dialogMode === 'create' ? 'Lägg till användare' : 'Redigera användare'}
            </DialogTitle>
            <DialogDescription className="font-ibm">
              {dialogMode === 'create'
                ? 'Skapa en ny användare med roll och skoltillhörighet'
                : 'Uppdatera användarens uppgifter'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Namn</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                placeholder="Anna Andersson"
                data-testid="user-form-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">E-post</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => setFormData(f => ({ ...f, email: e.target.value }))}
                placeholder="anna@skola.se"
                data-testid="user-form-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">
                Lösenord {dialogMode === 'edit' && <span className="normal-case text-slate-400">(lämna tomt för att behålla)</span>}
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={e => setFormData(f => ({ ...f, password: e.target.value }))}
                placeholder={dialogMode === 'edit' ? '••••••••' : 'Minst 6 tecken'}
                data-testid="user-form-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Roll</Label>
              <Select value={formData.role} onValueChange={v => setFormData(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin() && <SelectItem value="superadmin">Superadmin</SelectItem>}
                  <SelectItem value="school_admin">Skoladmin</SelectItem>
                  <SelectItem value="staff">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Skola</Label>
              <Select value={formData.school_id || '__none__'} onValueChange={v => setFormData(f => ({ ...f, school_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger data-testid="user-form-school">
                  <SelectValue placeholder="Välj skola" />
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin() && <SelectItem value="__none__">Alla skolor (ingen begränsning)</SelectItem>}
                  {schools.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {dialogMode === 'edit' && (
              <div className="flex items-center justify-between py-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Aktiv</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={v => setFormData(f => ({ ...f, is_active: v }))}
                  data-testid="user-form-active"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-ibm">
              Avbryt
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-navy hover:bg-navy/90 font-ibm gap-2" data-testid="user-form-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {dialogMode === 'create' ? 'Skapa' : 'Spara'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">Ta bort användare</DialogTitle>
            <DialogDescription className="font-ibm">
              Är du säker på att du vill ta bort <strong>{deletingUser?.name}</strong>?
              Denna åtgärd kan inte ångras.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)} className="font-ibm">
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="font-ibm gap-2"
              data-testid="confirm-delete-user"
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Ta bort
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
